import type {
  BulkAccessCopiesChoice,
  BulkAccessItemResult,
  BulkAccessMode,
  BulkAccessPreview,
  BulkAccessPreviewRequest,
  BulkAccessRequest,
  BulkAccessResult,
  BulkAccessScope,
  BulkAccessTarget,
} from '~/features/access-control/types/AccessControl';

import type { ServerAccessGrant } from './accessGrantMapper';

import { mapGrantToServer, mapServerGrant } from './accessGrantMapper';

/**
 * PROVISIONAL: the bulk access endpoints do not exist yet, so the `Server*`
 * shapes below are our proposed contract rather than an agreed one. This module
 * is the only place that knows the wire format — the feature layer deals purely
 * in the camelCase domain types — so adapting to the real API should be limited
 * to these types, the mappers here, and the two request paths in
 * `getBulkAccessPreview.ts` / `bulkUpdateAccess.ts`.
 */
type ServerBulkAccessTarget = {
  id: string;
  type: 'folder' | 'document';
  name: string;
};

export type ServerBulkAccessPreview = {
  folder_count: number;
  document_count: number;
  mixed_state_count: number;
  copies_count: number;
};

type ServerBulkAccessItemResult = {
  id: string;
  type: 'folder' | 'document';
  name: string;
  prior_grants_removed?: number;
  access_summary?: string;
};

export type ServerBulkAccessResult = {
  mode: BulkAccessMode;
  scope: BulkAccessScope;
  updated_count: number;
  items: ServerBulkAccessItemResult[];
  grants_applied: ServerAccessGrant[];
  location_label?: string;
};

export type ServerBulkAccessRequest = {
  targets: ServerBulkAccessTarget[];
  select_all?: boolean;
  folder_id?: string;
  scope: BulkAccessScope;
  mode: BulkAccessMode;
  copies_choice: BulkAccessCopiesChoice;
  grants: ServerAccessGrant[];
};

export type ServerBulkAccessPreviewRequest = {
  targets: ServerBulkAccessTarget[];
  select_all?: boolean;
  folder_id?: string;
};

function mapTargetToServer(target: BulkAccessTarget): ServerBulkAccessTarget {
  return {
    id: target.id,
    type: target.type,
    name: target.name,
  };
}

/**
 * Select-all is only meaningful when the server can enumerate the selection, so
 * a missing `folderId` is a programming error rather than an empty selection.
 * Fail loudly here instead of posting `folder_id: undefined` and having the
 * server silently apply the change to nothing (or everything).
 */
function mapSelectAllToServer(
  selectAll: boolean | undefined,
  folderId: string | undefined,
): { select_all: true; folder_id: string } | undefined {
  if (!selectAll) {
    return undefined;
  }

  if (!folderId) {
    throw new Error(
      'Bulk access select-all requires a folderId so the server can resolve the selection.',
    );
  }

  return { select_all: true, folder_id: folderId };
}

function mapItemResultFromServer(
  item: ServerBulkAccessItemResult,
): BulkAccessItemResult {
  return {
    id: item.id,
    type: item.type,
    name: item.name,
    priorGrantsRemoved: item.prior_grants_removed,
    accessSummary: item.access_summary,
  };
}

export function mapBulkAccessPreviewRequest({
  folderId,
  selectAll,
  targets,
}: BulkAccessPreviewRequest): ServerBulkAccessPreviewRequest {
  return {
    targets: targets.map(mapTargetToServer),
    ...mapSelectAllToServer(selectAll, folderId),
  };
}

export function mapBulkAccessPreviewResponse(
  serverResponse: ServerBulkAccessPreview,
): BulkAccessPreview {
  return {
    folderCount: serverResponse.folder_count,
    documentCount: serverResponse.document_count,
    mixedStateCount: serverResponse.mixed_state_count,
    copiesCount: serverResponse.copies_count,
  };
}

export function mapBulkAccessRequest(
  request: BulkAccessRequest,
): ServerBulkAccessRequest {
  return {
    targets: request.targets.map(mapTargetToServer),
    scope: request.scope,
    mode: request.mode,
    copies_choice: request.copiesChoice,
    grants: request.grants.map(mapGrantToServer),
    ...mapSelectAllToServer(request.selectAll, request.folderId),
  };
}

export function mapBulkAccessResultResponse(
  serverResponse: ServerBulkAccessResult,
): BulkAccessResult {
  return {
    mode: serverResponse.mode,
    scope: serverResponse.scope,
    updatedCount: serverResponse.updated_count,
    items: serverResponse.items.map(mapItemResultFromServer),
    grantsApplied: serverResponse.grants_applied.map(mapServerGrant),
    locationLabel: serverResponse.location_label,
  };
}
